'use client';
/* eslint-disable tailwindcss/no-arbitrary-value */
import classNames from 'classnames';
import React from 'react';
import { useBodyScrollLock } from '../../hooks/useBodyScrollLock';
import { useModalDialog } from '../../hooks/useModalDialog';
import CloseIcon from '../../icons/Close';
import { useEventCallback } from '../../utils/useEventCallback';
import { Portal } from '../Portal/Portal';

export interface SlideUpModalHandle {
  open: () => void;
  close: () => void;
}

export interface SlideUpModalProps {
  initialIsOpen?: boolean;
  onClose?: () => void;
  hideCloseButton?: boolean;
  /** Icon-only, so it has no text to name it. Localise at the call site. */
  closeButtonLabel?: string;
  disableClosingByOverlay?: boolean;
  disableStickyMode?: boolean;
  title?: React.ReactNode;
  titleClassName?: string;
  children: React.ReactNode;
  rootClassName?: string;
  titleContainerClassName?: string;
  bodyClassName?: string;
  /**
   * Opt-in: renders the sheet as a modal dialog with this name — the page
   * behind it inert, focus kept inside, Escape closing it.
   */
  dialog?: {
    label: string;
    id?: string;
    returnFocusRef?: React.RefObject<HTMLElement | null>;
  };
}

export const SlideUpModal = React.forwardRef<
  SlideUpModalHandle,
  SlideUpModalProps
>((props, ref) => {
  const {
    initialIsOpen = false,
    hideCloseButton,
    closeButtonLabel,
    disableClosingByOverlay,
    disableStickyMode,
    title,
    titleClassName,
    children,
    rootClassName,
    titleContainerClassName,
    bodyClassName,
    dialog,
  } = props;
  const [isOpen, setOpen] = React.useState(initialIsOpen);
  const onClose = useEventCallback(props.onClose);

  const close = React.useCallback(() => {
    setOpen(false);
    onClose?.();
  }, [onClose]);

  useBodyScrollLock(isOpen);

  React.useImperativeHandle(
    ref,
    () => ({
      open: () => {
        setOpen(true);
      },
      close,
    }),
    [close],
  );

  const [root, setRoot] = React.useState<HTMLDivElement | null>(null);
  useModalDialog(dialog ? root : null, {
    active: isOpen,
    onEscape: close,
    returnFocusRef: dialog?.returnFocusRef,
  });

  if (!isOpen) return null;

  return (
    <Portal>
      <div ref={setRoot}>
        <div
          className="fixed inset-0 z-modal size-full opacity-50 bg-[#25252B]"
          onClick={disableClosingByOverlay ? undefined : close}
        />

        <div
          role={dialog ? 'dialog' : undefined}
          aria-modal={dialog ? true : undefined}
          aria-label={dialog?.label}
          id={dialog?.id}
          className={classNames(
            'fixed z-modal flex flex-col items-center bg-white',
            'left-1/2 max-h-[80vh] -translate-x-1/2 overflow-y-auto',
            disableStickyMode
              ? 'bottom-auto top-1/2 w-111 max-w-[90%] -translate-y-1/2 rounded-radius/card lg:w-123 xl:w-[554px]'
              : 'bottom-0 w-full rounded-t-radius/card md:bottom-auto md:top-1/2 md:w-111 md:max-w-[90%] md:-translate-y-1/2 md:rounded-radius/card lg:w-123 xl:w-[554px]',
            rootClassName,
          )}
        >
          {title || !hideCloseButton ? (
            <div
              className={classNames(
                'flex w-full items-center justify-between px-spacing/gap/x py-3 md:py-4',
                titleContainerClassName,
              )}
            >
              {title ? (
                <h2
                  className={classNames(
                    'mhg-alpha-typography-subheading-2 text-text/primary',
                    titleClassName,
                  )}
                >
                  {title}
                </h2>
              ) : null}
              {!hideCloseButton && (
                <div
                  tabIndex={0}
                  onClick={close}
                  // A `div role="button"` gets none of a real button's keyboard
                  // behaviour, so a named one would be discoverable and inert.
                  onKeyDown={(event) => {
                    if (event.key !== 'Enter' && event.key !== ' ') return;
                    event.preventDefault();
                    close();
                  }}
                  className="text-text/primary"
                  role="button"
                  aria-label={closeButtonLabel ?? 'Close'}
                >
                  <CloseIcon className="size-6" />
                </div>
              )}
            </div>
          ) : null}

          <div
            // eslint-disable-next-line tailwindcss/no-unnecessary-arbitrary-value -- inherited from main; not in scope of CMS-1747
            className={classNames(
              'mhg-alpha-typography-caption w-full overflow-y-auto text-[#616161]',
              'flex-1',
              'py-4 md:py-5 lg:py-6',
              'md:max-h-[90vh] md:flex-none',
              'no-scrollbar',
              'px-4 lg:px-6',
              bodyClassName,
            )}
          >
            {children}
          </div>
        </div>
      </div>
    </Portal>
  );
});
SlideUpModal.displayName = 'SlideUpModal';
